import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  LessonBlockTranslationFields,
  LessonBlockTranslationProjection,
  LessonBlockTranslationRecord,
} from '@course-authoring/shared-types';
import {
  DEFAULT_AUTOSAVE_INTERVAL_MS,
  useAutosaveInterval,
  useLatestRef,
  useScopeAwareAutosaveLifecycle,
} from '../../hooks/useScopeAwareAutosaveLifecycle';

type FieldsByBlockId = Record<string, LessonBlockTranslationFields>;

const clone = <T,>(value: T): T => structuredClone(value);
const equal = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

export function buildLessonBlockTranslationPatch(
  baseline: LessonBlockTranslationFields,
  current: LessonBlockTranslationFields,
): LessonBlockTranslationFields {
  const patch: LessonBlockTranslationFields = {};
  for (const key of Object.keys(current)) {
    if (!equal(current[key], baseline[key])) patch[key] = clone(current[key]);
  }
  return patch;
}

function fieldsFromProjection(projection: LessonBlockTranslationProjection): FieldsByBlockId {
  return Object.fromEntries(projection.blocks.map((block) => [block.blockId, clone(block.fields)]));
}

export interface UseLessonBlockTranslationAutosaveOptions {
  enabled: boolean;
  scopeKey: string;
  projection: LessonBlockTranslationProjection | undefined;
  save: (
    blockId: string,
    fields: LessonBlockTranslationFields,
  ) => Promise<LessonBlockTranslationRecord>;
  onSaved?: (record: LessonBlockTranslationRecord) => void;
  intervalMs?: number;
}

export function useLessonBlockTranslationAutosave({
  enabled,
  scopeKey,
  projection,
  save,
  onSaved,
  intervalMs = DEFAULT_AUTOSAVE_INTERVAL_MS,
}: UseLessonBlockTranslationAutosaveOptions) {
  const [drafts, setDrafts] = useState<FieldsByBlockId>({});
  const {
    status,
    error,
    captureScope,
    isCurrentScope,
    beginSave,
    markSaved,
    markFailed,
    resetFeedback,
  } = useScopeAwareAutosaveLifecycle(scopeKey);
  const baselineRef = useRef<FieldsByBlockId>({});
  const draftsRef = useRef<FieldsByBlockId>({});
  const inFlightRef = useRef(new Map<string, Promise<void>>());
  const saveRef = useLatestRef(save);
  const onSavedRef = useLatestRef(onSaved);

  useEffect(() => {
    baselineRef.current = {};
    draftsRef.current = {};
    setDrafts({});
  }, [scopeKey]);

  useEffect(() => {
    if (!projection || projection.locale === 'en') return;
    const serverFields = fieldsFromProjection(projection);
    const nextDrafts: FieldsByBlockId = {};
    for (const [blockId, fields] of Object.entries(serverFields)) {
      const previousBaseline = baselineRef.current[blockId];
      const current = draftsRef.current[blockId];
      nextDrafts[blockId] = previousBaseline && current && !equal(previousBaseline, current)
        ? current
        : fields;
    }
    baselineRef.current = serverFields;
    draftsRef.current = nextDrafts;
    setDrafts(nextDrafts);
    resetFeedback();
  }, [projection, resetFeedback, scopeKey]);

  const persistBlock = useCallback(async function persist(blockId: string): Promise<void> {
    const existing = inFlightRef.current.get(blockId);
    if (existing) {
      await existing;
      return persist(blockId);
    }
    const baseline = baselineRef.current[blockId];
    const current = draftsRef.current[blockId];
    if (!baseline || !current) return;
    const patch = buildLessonBlockTranslationPatch(baseline, current);
    if (Object.keys(patch).length === 0) return;
    const submittedScope = captureScope();
    beginSave();

    let request: Promise<void>;
    request = saveRef.current(blockId, patch)
      .then((record) => {
        if (!isCurrentScope(submittedScope)) return;
        const serverFields = clone(record.fields);
        const latest = draftsRef.current[blockId] ?? current;
        baselineRef.current[blockId] = serverFields;
        const merged = { ...serverFields };
        for (const [key, value] of Object.entries(latest)) {
          if (!equal(value, current[key])) merged[key] = value;
        }
        draftsRef.current = { ...draftsRef.current, [blockId]: merged };
        setDrafts(draftsRef.current);
        markSaved(submittedScope);
        onSavedRef.current?.(record);
      })
      .catch((reason: unknown) => {
        markFailed(submittedScope, reason, 'Failed to save translation');
        throw reason;
      })
      .finally(() => {
        if (inFlightRef.current.get(blockId) === request) inFlightRef.current.delete(blockId);
      });
    inFlightRef.current.set(blockId, request);
    return request;
  }, [beginSave, captureScope, isCurrentScope, markFailed, markSaved]);

  const flush = useCallback(async () => {
    while (true) {
      const ids = Object.keys(draftsRef.current).filter((blockId) => {
        const baseline = baselineRef.current[blockId];
        return baseline && !equal(baseline, draftsRef.current[blockId]);
      });
      await Promise.all(ids.map(persistBlock));
      await Promise.all(inFlightRef.current.values());
      const stillDirty = Object.keys(draftsRef.current).some((blockId) => {
        const baseline = baselineRef.current[blockId];
        return baseline && !equal(baseline, draftsRef.current[blockId]);
      });
      if (!stillDirty) return;
    }
  }, [persistBlock]);

  useAutosaveInterval(enabled && Boolean(projection), intervalMs, () => {
    void flush().catch(() => undefined);
  });

  const updateBlockFields = useCallback((
    blockId: string,
    update: LessonBlockTranslationFields,
  ) => {
    const current = draftsRef.current[blockId] ?? {};
    const next = { ...current, ...clone(update) };
    draftsRef.current = { ...draftsRef.current, [blockId]: next };
    setDrafts(draftsRef.current);
    resetFeedback();
  }, [resetFeedback]);

  return {
    drafts,
    status,
    error,
    updateBlockFields,
    flush,
  };
}
